import { useMemo } from "react";
import { map, each, reduce, keys } from "underscore";
import { titleCase, startCase, internationalizeNumericValue } from "@ts/utils";
import {
    AVAILABILITY_CROSS_CHANNEL_WIDGET_METRICS,
    CATEGORY_AVAILABILITY_ECOMM_COMPARISON_WIDGET_METRICS,
    CATEGORY_AVAILABILITY_ECOMM_COMPETITION_WIDGET_METRICS,
    CATEGORY_OVERVIEW_CROSS_CHANNEL_COMPETITION_WIDGET_METRICS,
    CROSS_CHANNEL_WIDGET_METRICS,
    KEYWORD_VISBILITY_CROSS_CHANNEL_WIDGET_METRICS,
    PRICING_CROSS_CHANNEL_WIDGET_METRICS,
    VISBILITY_CROSS_CHANNEL_WIDGET_METRICS,
} from "@ts/constants";
import { calculateChangePerc } from "@ts/widget";
import {
    CrossChannelTableDataModules,
    CrossChannelTableDataModuleSections,
    DeepDiveModuleType,
} from "@ts/common-types";
import { CHANNEL_CONFIGS } from "@/config/channel-configs";

const useCrossChannelTableData = (
    data: any[] | undefined | null,
    expandedData: any[] | undefined | null,
    type = CrossChannelTableDataModules.REVIEW,
    section?: CrossChannelTableDataModuleSections,
    deepDiveModuleType = DeepDiveModuleType.PRODUCT
) => {
    // Helper: generate row content for a cell.
    const generateCrossChannelTableRowContent = (
        current: number | undefined | null,
        previous: number | undefined | null
    ) => {
        if (current == null || previous == null)
            return <div className="text-end">N/A</div>;

        const isChangeApplicable =
            typeof current === "number" && typeof previous === "number";

        const change = isChangeApplicable
            ? calculateChangePerc(current, previous)
            : null;
        return (
            <div className="text-end">
                <span>
                    {typeof current === "number"
                        ? internationalizeNumericValue(current.toFixed(1))
                        : current}
                </span>{" "}
                {isChangeApplicable && (
                    <span
                        className={change < 0 ? "text-danger" : "text-success"}
                        style={{ marginLeft: "8px" }}
                    >
                        {`${change < 0 ? "↓" : "↑"}${Math.abs(change)}%`}
                    </span>
                )}
            </div>
        );
    };

    const tableData = useMemo(() => {
        if (!data || !Array.isArray(data)) return [];

        let metrics;
        // Use switch-case to choose the metrics array based on type.
        switch (type) {
            case CrossChannelTableDataModules.REVIEW:
                metrics = CROSS_CHANNEL_WIDGET_METRICS;
                break;
            case CrossChannelTableDataModules.PRICING:
                metrics = PRICING_CROSS_CHANNEL_WIDGET_METRICS;
                break;
            case CrossChannelTableDataModules.VISIBILITY:
                if (
                    section ===
                    CrossChannelTableDataModuleSections.KEYWORD_VISIBILITY
                ) {
                    metrics = KEYWORD_VISBILITY_CROSS_CHANNEL_WIDGET_METRICS;
                } else {
                    metrics = VISBILITY_CROSS_CHANNEL_WIDGET_METRICS;
                }
                break;
            case CrossChannelTableDataModules.AVAILABILITY:
                if (
                    section ===
                    CrossChannelTableDataModuleSections.CATEGORY_AVAILABILITY_COMPETITION_ECOMM
                ) {
                    metrics =
                        CATEGORY_AVAILABILITY_ECOMM_COMPETITION_WIDGET_METRICS;
                } else if (
                    section ===
                    CrossChannelTableDataModuleSections.CATEGORY_AVAILABILITY_COMPARISON_ECOMM
                ) {
                    metrics =
                        CATEGORY_AVAILABILITY_ECOMM_COMPARISON_WIDGET_METRICS;
                } else {
                    metrics = AVAILABILITY_CROSS_CHANNEL_WIDGET_METRICS;
                }
                break;
            case CrossChannelTableDataModules.OVERVIEW:
                metrics =
                    CATEGORY_OVERVIEW_CROSS_CHANNEL_COMPETITION_WIDGET_METRICS;
                break;
            default:
                metrics = PRICING_CROSS_CHANNEL_WIDGET_METRICS;
                break;
        }

        return map(metrics, (metric, index) => {
            const row: Record<string, any> = {
                key: (index + 1).toString(),
                metrics: metric.label,
            };

            each(data, (entity: any, idx: number) => {
                const current = entity?.[metric.currentKey] || 0;
                const previous = entity?.[metric.previousKey] || 0;

                row[`channel${idx + 1}`] = generateCrossChannelTableRowContent(
                    current,
                    previous
                );

                // Use a switch to decide how to get the channel name.
                let channelName: string;
                switch (type) {
                    case CrossChannelTableDataModules.REVIEW:
                        channelName =
                            CHANNEL_CONFIGS[entity.channel_id].name ||
                            "Unknown";
                        break;
                    case CrossChannelTableDataModules.PRICING:
                        channelName = entity.name;
                        break;
                    case CrossChannelTableDataModules.VISIBILITY:
                        channelName = entity.channel;
                        break;
                    case CrossChannelTableDataModules.AVAILABILITY:
                        if (
                            [
                                CrossChannelTableDataModuleSections.CATEGORY_AVAILABILITY_COMPETITION_ECOMM,
                                CrossChannelTableDataModuleSections.CATEGORY_AVAILABILITY_COMPARISON_ECOMM,
                            ].includes(section)
                        ) {
                            channelName = entity.brand || "Unknown";
                        } else {
                            channelName = entity.channel;
                        }
                        break;
                    case CrossChannelTableDataModules.OVERVIEW:
                        if (
                            section ===
                            CrossChannelTableDataModuleSections.CATEGORY_OVERVIEW_CROSS_CHANNEL
                        ) {
                            channelName = entity.name;
                        } else if (
                            section ===
                            CrossChannelTableDataModuleSections.CATEGORY_OVERVIEW_COMPETITION
                        ) {
                            channelName = entity.brand;
                        } else if (
                            section ===
                            CrossChannelTableDataModuleSections.CATEGORY_OVERVIEW_COMPARISON
                        ) {
                            channelName = entity.name;
                        } else {
                            channelName = entity.channel;
                        }

                        break;
                    default:
                        channelName = entity.channel;
                        break;
                }
                row[`channelName${idx + 1}`] = titleCase(channelName);
            });

            return row;
        });
    }, [data, type]);

    const expandedTableData = useMemo(() => {
        if (!expandedData || !Array.isArray(expandedData)) return [];

        const groupedData = reduce(
            expandedData,
            (acc: Record<string, any[]>, item: any) => {
                let tableMetric: string;
                switch (type) {
                    case CrossChannelTableDataModules.REVIEW:
                        tableMetric = item?.sentiment;
                        break;
                    case CrossChannelTableDataModules.PRICING:
                        tableMetric = `${item?.sku_title}-[${item?.sku}]`;
                        break;
                    case CrossChannelTableDataModules.VISIBILITY:
                        tableMetric = item?.keyword || "Unknown";
                        break;
                    default:
                        tableMetric = `${item?.sku_title}-[${item?.sku}]`;
                        break;
                }
                tableMetric = tableMetric || "Unknown";
                if (!acc[tableMetric]) acc[tableMetric] = [];
                acc[tableMetric].push(item);
                return acc;
            },
            {}
        );

        return map(keys(groupedData), (tableMetric, index) => {
            const row: Record<string, any> = {
                key: (index + 1).toString(),
                metrics:
                    type === CrossChannelTableDataModules.REVIEW
                        ? `${startCase(tableMetric)} Score (%)`
                        : tableMetric,
            };

            each(groupedData[tableMetric], (item: any, idx: number) => {
                let current: number, previous: number;
                switch (type) {
                    case CrossChannelTableDataModules.REVIEW:
                        current = item?.curr_nps_score || 0;
                        previous = item?.pre_nps_score || 0;
                        break;
                    case CrossChannelTableDataModules.PRICING:
                        current = item?.curr_price || 0;
                        previous = item?.prev_price || 0;
                        break;
                    case CrossChannelTableDataModules.VISIBILITY:
                        current = item?.cur_overall_sov || 0;
                        previous = item?.prev_overall_sov || 0;
                        break;
                    default:
                        current = item?.curr_price || 0;
                        previous = item?.prev_price || 0;
                        break;
                }
                row[`channel${idx + 1}`] = generateCrossChannelTableRowContent(
                    current,
                    previous
                );
            });

            return row;
        });
    }, [expandedData, type]);

    return { tableData, expandedTableData };
};

export default useCrossChannelTableData;
